import { useState, useCallback, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useCurrency } from '@/hooks/useCurrency';
import { useDisplayFx } from '@/hooks/useDisplayFx';
import { convertUsdForDisplay } from '@/lib/displayFx';
import DualCurrencyMoney from '@/components/base/DualCurrencyMoney';
import type { ParsedProduct, ViewMode, FilterState } from '@/hooks/useProducts';

function getCapacityDisplay(product: ParsedProduct): string | null {
  if (product.capacityMlTag) return `${product.capacityMlTag}ml`;
  if (product.capacityMl) return `${product.capacityMl}ml`;
  return product.capacity;
}

function getLeadTimeDaysDisplay(product: ParsedProduct, t: (key: string, opts?: object) => string): string | null {
  if (product.leadTimeMinDays != null && product.leadTimeMaxDays != null) {
    return t('products.card.leadTimeDaysRange', { min: product.leadTimeMinDays, max: product.leadTimeMaxDays });
  }
  if (product.leadTimeMinDays != null) return t('products.card.leadTimeDaysSingle', { days: product.leadTimeMinDays });
  if (product.leadTimeMaxDays != null) return t('products.card.leadTimeDaysSingle', { days: product.leadTimeMaxDays });
  return product.leadTimeDisplay || null;
}

function getAvailabilityDisplay(product: ParsedProduct, t: (key: string, opts?: object) => string): string | null {
  if (!product.availabilityStatus) return null;
  return t(`products.availability.${product.availabilityStatus}`);
}

interface ProductResultsProps {
  products: ParsedProduct[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  viewMode: ViewMode;
  loading: boolean;
  onClearFilters: () => void;
  filters: FilterState;
}

export default function ProductResults({
  products,
  totalCount,
  currentPage,
  totalPages,
  pageSize,
  onPageChange,
  viewMode,
  loading,
  onClearFilters,
  filters,
}: ProductResultsProps) {
  const { t } = useTranslation('products');
  const { user } = useAuth();
  const { currency } = useCurrency();
  const { rate } = useDisplayFx(currency);
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());
  const [loginPromptId, setLoginPromptId] = useState<string | null>(null);

  const toggleSave = useCallback(
    (productId: string) => {
      if (!user) {
        setLoginPromptId(productId);
        setTimeout(() => setLoginPromptId((prev) => (prev === productId ? null : prev)), 2000);
        return;
      }
      setSavedIds((prev) => {
        const next = new Set(prev);
        if (next.has(productId)) {
          next.delete(productId);
        } else {
          next.add(productId);
        }
        return next;
      });
    },
    [user]
  );

  const startIdx = currentPage * pageSize + 1;
  const endIdx = Math.min(startIdx + products.length - 1, totalCount);

  if (loading) {
    return (
      <div className="px-4 md:px-6 lg:px-10 py-4 md:py-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
          {Array.from({ length: 16 }).map((_, i) => (
            <div key={i} className="bg-white rounded-lg border border-ink-100 overflow-hidden animate-pulse">
              <div className="aspect-square bg-cream-200" />
              <div className="p-4 space-y-2">
                <div className="h-3 bg-cream-200 rounded w-16" />
                <div className="h-4 bg-cream-200 rounded w-3/4" />
                <div className="h-3 bg-cream-200 rounded w-1/2" />
                <div className="h-8 bg-cream-200 rounded w-full mt-2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (products.length === 0) {
    // Availability-specific empty state message
    let emptyMessage = t('products.empty.message');
    if (filters.availabilityStatus === 'available') {
      emptyMessage = t('products.empty.noAvailable');
    } else if (filters.availabilityStatus === 'limited_stock') {
      emptyMessage = t('products.empty.noLimitedStock');
    } else if (filters.availabilityStatus === 'check_availability') {
      emptyMessage = t('products.empty.noCheckAvailability');
    } else if (filters.availabilityStatus === 'made_to_order') {
      emptyMessage = t('products.empty.noMadeToOrder');
    }

    return (
      <div className="flex flex-col items-center justify-center py-16 md:py-24 px-4">
        <div className="w-16 h-16 rounded-full bg-cream-100 flex items-center justify-center mb-4">
          <i className="ri-search-line text-3xl text-ink-300" />
        </div>
        <h3 className="text-lg font-semibold text-ink-700 mb-2">{t('products.empty.title')}</h3>
        <p className="text-sm text-ink-400 text-center max-w-md mb-6">
          {emptyMessage}
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={onClearFilters}
            className="px-5 py-2.5 bg-ink-700 text-white text-sm font-medium rounded-lg hover:bg-ink-600 transition-colors whitespace-nowrap"
          >
            {t('products.empty.clearFilters')}
          </button>
          <a
            href="/image-search"
            className="px-5 py-2.5 border border-ink-300 text-ink-600 text-sm font-medium rounded-lg hover:bg-cream-50 transition-colors whitespace-nowrap inline-flex items-center justify-center gap-2"
          >
            <i className="ri-image-line text-base" />
            {t('products.empty.imageSearch')}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-6 lg:px-10 py-4 md:py-6">
      {/* Showing count */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-ink-400">
          {t('products.pagination.showing', { start: startIdx, end: endIdx, total: totalCount })}
        </p>
      </div>

      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
          {products.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              isSaved={savedIds.has(product.id)}
              onToggleSave={() => toggleSave(product.id)}
              showLoginPrompt={loginPromptId === product.id}
              displayCurrency={currency}
              displayFxRate={rate}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {products.map((product) => (
            <ProductListItem
              key={product.id}
              product={product}
              isSaved={savedIds.has(product.id)}
              onToggleSave={() => toggleSave(product.id)}
              showLoginPrompt={loginPromptId === product.id}
              displayCurrency={currency}
              displayFxRate={rate}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <PaginationBar
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
}

// ---- Pagination Bar ----
function PaginationBar({
  currentPage,
  totalPages,
  onPageChange,
}: {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}) {
  const { t } = useTranslation('products');

  const getPages = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible + 2) {
      for (let i = 0; i < totalPages; i++) pages.push(i);
      return pages;
    }

    pages.push(0);

    let start = Math.max(1, currentPage - 1);
    let end = Math.min(totalPages - 2, currentPage + 1);

    if (currentPage <= 2) {
      end = Math.min(totalPages - 2, 3);
    }
    if (currentPage >= totalPages - 3) {
      start = Math.max(1, totalPages - 4);
    }

    if (start > 1) pages.push('...');
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages - 2) pages.push('...');

    pages.push(totalPages - 1);
    return pages;
  };

  const pages = getPages();

  const handleClick = (page: number) => {
    onPageChange(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="flex items-center justify-center gap-1.5 mt-8 pt-4 border-t border-ink-100">
      <button
        onClick={() => handleClick(currentPage - 1)}
        disabled={currentPage === 0}
        className="px-3 py-1.5 rounded-md text-sm font-medium text-ink-600 hover:bg-cream-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
      >
        {t('products.pagination.prev')}
      </button>

      {pages.map((p, idx) => {
        if (p === '...') {
          return (
            <span key={`ellipsis-${idx}`} className="px-2 py-1.5 text-sm text-ink-300 select-none">
              ...
            </span>
          );
        }
        const pageNum = p as number;
        const isActive = pageNum === currentPage;
        return (
          <button
            key={pageNum}
            onClick={() => handleClick(pageNum)}
            className={`min-w-[2.25rem] px-3 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
              isActive
                ? 'bg-ink-700 text-white'
                : 'text-ink-600 hover:bg-cream-100'
            }`}
          >
            {pageNum + 1}
          </button>
        );
      })}

      <button
        onClick={() => handleClick(currentPage + 1)}
        disabled={currentPage >= totalPages - 1}
        className="px-3 py-1.5 rounded-md text-sm font-medium text-ink-600 hover:bg-cream-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
      >
        {t('products.pagination.next')}
      </button>
    </div>
  );
}

// ---- Availability Badge ----
function AvailabilityBadge({ status }: { status: ParsedProduct['availabilityStatus'] }) {
  const { t } = useTranslation('products');
  if (!status) return null;

  const styles: Record<string, string> = {
    available: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    limited_stock: 'bg-amber-50 border-amber-200 text-amber-700',
    check_availability: 'bg-slate-100 border-slate-200 text-slate-600',
    made_to_order: 'bg-ink-50 border-ink-200 text-ink-600',
  };

  const icons: Record<string, string> = {
    available: 'ri-check-line',
    limited_stock: 'ri-alarm-warning-line',
    check_availability: 'ri-question-line',
    made_to_order: 'ri-time-line',
  };

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 border text-[10px] font-semibold rounded ${styles[status]}`}>
      <i className={icons[status]} />
      {t(`products.availability.${status}`)}
    </span>
  );
}

// ---- Similarity Badge (subtle) ----
function SimilarityBadge({ value }: { value: number }) {
  const percent = Math.round(value * 100);
  return (
    <span className="inline-flex items-center justify-center gap-0.5 px-1.5 py-0.5 bg-blue-900 text-white text-[10px] font-semibold rounded whitespace-nowrap">
      <i className="ri-swap-line text-[9px] text-white/90" />
      {percent}%
    </span>
  );
}

// ---- Product Card ----
function ProductCard({
  product,
  isSaved,
  onToggleSave,
  showLoginPrompt,
  displayCurrency,
  displayFxRate,
}: {
  product: ParsedProduct;
  isSaved: boolean;
  onToggleSave: () => void;
  showLoginPrompt: boolean;
  displayCurrency: string;
  displayFxRate: number | null;
}) {
  const { t } = useTranslation('products');
  const navigate = useNavigate();
  const [imgError, setImgError] = useState(false);
  const [imgIndex, setImgIndex] = useState(0);

  // Build list of all candidate image URLs (primary + fallbacks from edge function)
  const rawTags = product.tags as Record<string, unknown> | null;
  const allImages = [
    product.imageUrl,
    (rawTags?.source_image_url as string),
    (rawTags?.image_url as string),
    ...((rawTags?._imageUrls as string[] | undefined) || []),
  ].filter((url): url is string => !!url && typeof url === 'string');

  const imgSrc = allImages[imgIndex] || null;

  const handleImageError = useCallback(() => {
    if (imgIndex < allImages.length - 1) {
      setImgIndex((prev) => prev + 1);
    } else {
      setImgError(true);
    }
  }, [imgIndex, allImages.length]);

  return (
    <div
      className="group bg-white rounded-lg border border-ink-100 overflow-hidden hover:border-ink-300 transition-all duration-200 flex flex-col cursor-pointer hover:-translate-y-0.5"
      onClick={() => navigate(`/products/${product.id}`)}
    >
      {/* Image */}
      <div className="relative aspect-square bg-white overflow-hidden">
        {!imgError && imgSrc ? (
          <img
            src={imgSrc}
            alt={product.name}
            className="w-full h-full object-contain p-4 pt-10 group-hover:scale-[1.02] transition-transform duration-300"
            onError={handleImageError}
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center bg-cream-50 p-4">
            <i className="ri-box-3-line text-4xl text-ink-200 mb-2" />
            <span className="text-xs text-ink-300">{t('products.card.noImage')}</span>
          </div>
        )}

        {/* SKU & similarity — top left */}
        <div className="absolute top-3 left-3 z-10 flex flex-col gap-1">
          <span className="text-[10px] font-medium text-ink-400">
            SKU: {product.sku ?? '—'}
          </span>
          {product.similarity != null && (
            <SimilarityBadge value={product.similarity} />
          )}
        </div>

        {/* Save button */}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onToggleSave();
          }}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/90 border border-ink-100 flex items-center justify-center hover:bg-white transition-all shadow-sm"
        >
          <i className={`${isSaved ? 'ri-heart-fill text-red-500' : 'ri-heart-line text-ink-400'} text-base`} />
        </button>

        {showLoginPrompt && (
          <div className="absolute top-3 right-12 bg-ink-700 text-white text-xs px-2.5 py-1.5 rounded-md shadow-lg whitespace-nowrap z-10 animate-fade-in">
            {t('products.card.loginToSave')}
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-4 flex flex-col flex-1">

        <h4 className="text-sm font-semibold text-ink-700 line-clamp-2 mb-1 leading-snug min-h-[2.5rem]">
          {product.name}
        </h4>

        <SpecsRow product={product} displayCurrency={displayCurrency} displayFxRate={displayFxRate} />

        {/* Product kind badge */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs mt-auto">
          {product.productKind === 'finished' && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-semibold rounded">
              <i className="ri-check-double-line text-[10px]" />
              {t('products.card.finishedProduct')}
            </span>
          )}
          {product.deliverDays != null && (
            <span className="text-ink-400 inline-flex items-center gap-1">
              <i className="ri-truck-line text-[10px]" />
              {t('products.card.delivery', { days: product.deliverDays })}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- Product List Item ----
function ProductListItem({
  product,
  isSaved,
  onToggleSave,
  showLoginPrompt,
  displayCurrency,
  displayFxRate,
}: {
  product: ParsedProduct;
  isSaved: boolean;
  onToggleSave: () => void;
  showLoginPrompt: boolean;
  displayCurrency: string;
  displayFxRate: number | null;
}) {
  const { t } = useTranslation('products');
  const navigate = useNavigate();
  const [imgError, setImgError] = useState(false);
  const [imgIndex, setImgIndex] = useState(0);

  // Build list of all candidate image URLs (primary + fallbacks from edge function)
  const rawTags = product.tags as Record<string, unknown> | null;
  const allImages = [
    product.imageUrl,
    (rawTags?.source_image_url as string),
    (rawTags?.image_url as string),
    ...((rawTags?._imageUrls as string[] | undefined) || []),
  ].filter((url): url is string => !!url && typeof url === 'string');

  const imgSrc = allImages[imgIndex] || null;

  const handleImageError = useCallback(() => {
    if (imgIndex < allImages.length - 1) {
      setImgIndex((prev) => prev + 1);
    } else {
      setImgError(true);
    }
  }, [imgIndex, allImages.length]);

  return (
    <div
      className="flex flex-col sm:flex-row gap-4 bg-white rounded-lg border border-ink-100 p-3 md:p-4 hover:border-ink-300 transition-all duration-200 cursor-pointer hover:-translate-y-0.5"
      onClick={() => navigate(`/products/${product.id}`)}
    >
      {/* Thumbnail */}
      <div className="relative w-full sm:w-28 md:w-32 shrink-0">
        <div className="aspect-square bg-white rounded-md overflow-hidden border border-ink-100">
          {!imgError && imgSrc ? (
            <img
              src={imgSrc}
              alt={product.name}
              className="w-full h-full object-contain p-2"
              onError={handleImageError}
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-cream-50">
              <i className="ri-box-3-line text-2xl text-ink-200" />
            </div>
          )}
        </div>
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              {product.mappedCategory && (
                <span className="inline-block px-2 py-0.5 bg-cream-100 text-ink-500 text-[10px] font-medium rounded border border-ink-100">
                  {t(`products.category.${product.mappedCategory}`)}
                </span>
              )}
              {product.similarity != null && (
                <SimilarityBadge value={product.similarity} />
              )}
              <AvailabilityBadge status={product.availabilityStatus} />
            </div>
            <h4 className="text-sm font-semibold text-ink-700 truncate">{product.name}</h4>
          </div>
          <div className="relative shrink-0">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onToggleSave();
              }}
              className="w-8 h-8 rounded-full bg-cream-50 border border-ink-100 flex items-center justify-center hover:bg-cream-100 transition-all"
            >
              <i className={`${isSaved ? 'ri-heart-fill text-red-500' : 'ri-heart-line text-ink-400'} text-base`} />
            </button>
            {showLoginPrompt && (
              <div className="absolute top-full right-0 mt-1 bg-ink-700 text-white text-xs px-2.5 py-1.5 rounded-md shadow-lg whitespace-nowrap z-10">
                {t('products.card.loginToSave')}
              </div>
            )}
          </div>
        </div>

        <p className="text-[11px] text-ink-300 mb-2">
          {product.sku ? t('products.card.sku', { sku: product.sku }) : t('products.card.noSku')}
        </p>

        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-400 mb-3">
          {product.materialHint && <span>{t('products.specs.material')}: {product.materialHint}</span>}
          {product.mappedCategory && <span>{t('products.specs.shape')}: {t(`products.category.${product.mappedCategory}`)}</span>}
        </div>

        <SpecsRow product={product} displayCurrency={displayCurrency} displayFxRate={displayFxRate} />

        {/* Product kind + delivery badge */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs mb-3">
          {product.productKind === 'finished' && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-semibold rounded">
              <i className="ri-check-double-line text-[10px]" />
              {t('products.card.finishedProduct')}
            </span>
          )}
          {product.deliverDays != null && (
            <span className="text-ink-400 inline-flex items-center gap-1">
              <i className="ri-truck-line text-[10px]" />
              {t('products.card.delivery', { days: product.deliverDays })}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- Specs Row (below product name) ----
function SpecsRow({
  product,
  displayCurrency,
  displayFxRate,
}: {
  product: ParsedProduct;
  displayCurrency: string;
  displayFxRate: number | null;
}) {
  const { t } = useTranslation('products');

  const items: { label: string; node: ReactNode | null }[] = [
    { label: t('products.specs.capacity'), node: getCapacityDisplay(product) },
    { label: 'MOQ', node: (product.moqTag != null && product.moqTag > 0) ? product.moqTag.toLocaleString() : (product.moqDisplay || ((product.moq != null && product.moq > 0) ? `${product.moq.toLocaleString()}` : t('products.card.moqFree'))) },
    { label: t('products.card.orderMethod'), node: getAvailabilityDisplay(product, t) },
    { label: t('products.card.deliveryLabel'), node: getLeadTimeDaysDisplay(product, t) },
    ...(product.displayPriceText
      ? [
          {
            label: t('products.card.basePrice'),
            node: (
              <DualCurrencyMoney
                usdAmount={product.displayPriceText}
                selectedAmount={convertUsdForDisplay(product.displayPriceText, displayFxRate)}
                selectedCurrency={displayCurrency}
                compact
                primaryClassName="text-[11px] font-semibold text-ink-700"
                secondaryClassName="text-[10px] font-normal text-ink-400"
              />
            ),
          },
        ]
      : []),
  ];

  const visibleItems = items.filter((item) => item.node != null);

  if (visibleItems.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] text-ink-400 mt-1.5 mb-2">
      {visibleItems.map((item, idx) => (
        <span key={item.label} className="inline-flex items-center gap-x-1 whitespace-nowrap">
          <span className="text-ink-500 font-medium">{item.label}:</span>
          <span className="text-ink-600">{item.node}</span>
          {idx < visibleItems.length - 1 && (
            <span className="text-ink-200 mx-1 select-none">|</span>
          )}
        </span>
      ))}
    </div>
  );
}